import { useEffect, useRef } from "react";

/**
 * Een taak uitvoeren via de tegel die erover gaat (Maarten, 2026-10-09: "alles moet via een taak
 * toegevoegd/gecontroleerd kunnen worden").
 *
 * <p>De tegel Taken weet niet hoe je een enting, een weging of een notitie vastlegt; de tegels Bescherming,
 * Gewichten en Notities wel, met hun eigen invoerscherm. Daarom vraagt Taken het: "voer deze taak uit". De tegel
 * die over die handeling gaat opent zijn invoerscherm, en meldt na het opslaan terug dat de taak gedaan is. Dan zet
 * Taken hem op afgerond. Zo blijft elk invoerscherm op één plek, en hoeft Taken er geen kopie van te hebben.
 *
 * <p>Als geen tegel de vraag oppakt (die tegel staat uit), hoort de tegel Taken dat meteen terug en zegt hij het.
 */

/** Wat een taak doet als je hem uitvoert; komt van de api (TaakKeuzeService.HANDELINGEN). */
export type Handeling =
  | "ENTING" | "ONTWORMING" | "ONTVLOOIING" | "WEGING" | "VOEDING" | "CHIPREGISTRATIE" | "NOTITIE" | "DOCUMENT"
  | "WIZARD" | "VRIJ";

export interface Uitvoering {
  taakId: string;
  dierId: number;
  handeling: Handeling;
  /** De titel van de taak, voor het invoerscherm (bijv. de naam van de enting). */
  titel: string;
  /** Zet een tegel op true als hij de vraag oppakt. */
  opgepakt?: boolean;
}

const VRAAG = "fawkes:taak-uitvoeren";
const GEDAAN = "fawkes:taak-uitgevoerd";

/** Vraag de tegels om deze taak uit te voeren; geeft terug of er een tegel was die hem oppakte. */
export function vraagUitvoering(u: Uitvoering): boolean {
  const detail: Uitvoering = { ...u, opgepakt: false };
  window.dispatchEvent(new CustomEvent<Uitvoering>(VRAAG, { detail }));
  return detail.opgepakt === true;
}

/** Meld dat een taak gedaan is: het invoerscherm is opgeslagen. */
export function meldUitgevoerd(taakId: string) {
  window.dispatchEvent(new CustomEvent<{ taakId: string }>(GEDAAN, { detail: { taakId } }));
}

/**
 * Voor een tegel: pak de vragen op voor deze handelingen bij dit dier, en open je invoerscherm. De callback krijgt
 * de uitvoering; bewaar het taakId en roep {@link meldUitgevoerd} na het opslaan.
 */
export function useTaakUitvoering(dierId: number | string, handelingen: Handeling[], open: (u: Uitvoering) => void) {
  const openRef = useRef(open);
  openRef.current = open;
  const sleutel = handelingen.join(",");
  useEffect(() => {
    const luister = (e: Event) => {
      const u = (e as CustomEvent<Uitvoering>).detail;
      if (!u || u.opgepakt || String(u.dierId) !== String(dierId) || !sleutel.split(",").includes(u.handeling)) return;
      u.opgepakt = true;
      openRef.current(u);
    };
    window.addEventListener(VRAAG, luister);
    return () => window.removeEventListener(VRAAG, luister);
  }, [dierId, sleutel]);
}

/** Voor de tegel Taken: hoor wanneer een taak gedaan is. */
export function useTaakUitgevoerd(klaar: (taakId: string) => void) {
  const ref = useRef(klaar);
  ref.current = klaar;
  useEffect(() => {
    const luister = (e: Event) => {
      const id = (e as CustomEvent<{ taakId: string }>).detail?.taakId;
      if (id) ref.current(id);
    };
    window.addEventListener(GEDAAN, luister);
    return () => window.removeEventListener(GEDAAN, luister);
  }, []);
}

/**
 * Voor een tegel in één regel: pak de vraag op, open je invoerscherm met {@code open}, en roep {@code klaar()} na
 * het opslaan. Zolang {@code actief} (het invoerscherm staat open) onthoudt de tegel de taak; gaat het dicht zonder
 * opslaan, dan is de taak niet gedaan en vergeet hij hem.
 */
export function useTaakVoorTegel(
  dierId: number | string,
  handelingen: Handeling[],
  open: (u: Uitvoering) => void,
  actief: boolean,
): { klaar: () => void } {
  const taakId = useRef<string | null>(null);
  useTaakUitvoering(dierId, handelingen, (u) => {
    taakId.current = u.taakId;
    open(u);
  });
  useEffect(() => {
    if (!actief) taakId.current = null;
  }, [actief]);
  return {
    klaar: () => {
      if (taakId.current) {
        meldUitgevoerd(taakId.current);
        taakId.current = null;
      }
    },
  };
}
